import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart, useCartSummary, useCartToast } from '../../contexts';
import { CartItem } from '../molecules/CartItem';
import Button from '../atoms/Button';
import LogoSpinner from '../atoms/LogoSpinner';
import './CartDrawer.css';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onCheckout?: () => void;
  className?: string;
}

export const CartDrawer: React.FC<CartDrawerProps> = ({
  isOpen,
  onClose,
  onCheckout,
  className = ''
}) => {
  const navigate = useNavigate();
  const { cart, operations, loading, error } = useCart();
  const { itemCount, totalItems, subtotal, vat, vatPercentage, isInclusive, shipping, couponDiscount, total, isEmpty } = useCartSummary();
  const { showRemoveFromCartSuccess, showQuantityUpdated } = useCartToast();
  const drawerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);
  const [showVatBreakdown, setShowVatBreakdown] = useState(true);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Store the previously focused element
      previousActiveElement.current = document.activeElement as HTMLElement;
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Focus management
  useEffect(() => {
    if (isOpen && drawerRef.current) {
      // Focus the drawer when it opens
      drawerRef.current.focus();
    } else if (!isOpen && previousActiveElement.current) {
      // Restore focus when drawer closes
      previousActiveElement.current.focus();
    }
  }, [isOpen]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // VAT calculations are now handled automatically by CartOperations
  // No need for manual VAT updates here

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleCheckout = () => {
    if (onCheckout) {
      onCheckout();
    } else {
      // Default checkout behavior - could navigate to checkout page
      console.log('Proceeding to checkout...');
    }
  };

  const handleViewCart = () => {
    navigate('/cart');
    onClose();
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const renderEmptyState = () => (
    <div className="cart-drawer__empty">
      <div className="cart-drawer__empty-icon">
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.7 15.3C4.3 15.7 4.6 16.5 5.1 16.5H17M17 13V17C17 18.1 16.1 19 15 19H9C7.9 19 7 18.1 7 17V13M17 13H7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h3 className="cart-drawer__empty-title">Your cart is empty</h3>
      <p className="cart-drawer__empty-message">
        Add some delicious treats to get started!
      </p>
      <Button
        onClick={onClose}
        variant="primary"
        size="medium"
        className="cart-drawer__empty-button"
      >
        Continue Shopping
      </Button>
    </div>
  );

  const renderCartItems = () => (
    <div className="cart-drawer__items">
      {cart.items.map((item) => (
        <CartItem
          key={item.id}
          item={item}
          variant="compact"
          showRemoveButton={true}
          showQuantityControls={true}
        />
      ))}
    </div>
  );

  const renderSummary = () => (
    <div className="cart-drawer__summary">
      <div className="cart-drawer__summary-header">
        <h3 className="cart-drawer__summary-title">Order Summary</h3>
        <span className="cart-drawer__item-count">
          {totalItems} {totalItems === 1 ? 'item' : 'items'}
        </span>
        {vat > 0 && (
          <div className="cart-drawer__vat-toggle">
            <label className="cart-drawer__vat-toggle-label">
              <input
                type="checkbox"
                checked={showVatBreakdown}
                onChange={(e) => setShowVatBreakdown(e.target.checked)}
                className="cart-drawer__vat-toggle-input"
              />
              <span className="cart-drawer__vat-toggle-text">Show VAT</span>
            </label>
          </div>
        )}
      </div>

      <div className="cart-drawer__summary-details">
        <div className="cart-drawer__summary-line">
          <span>Subtotal</span>
          <span>{formatPrice(subtotal)}</span>
        </div>
        
        {vat > 0 && showVatBreakdown && (
          <div className="cart-drawer__summary-line">
            <span>
              {isInclusive ? `VAT (${vatPercentage}%) - Included` : `VAT (${vatPercentage}%)`}
            </span>
            <span>{formatPrice(vat)}</span>
          </div>
        )}
        
        <div className="cart-drawer__summary-line">
          <span>Shipping</span>
          <span>
            {shipping === 0 ? (
              <span className="cart-drawer__free-shipping">FREE</span>
            ) : (
              formatPrice(shipping)
            )}
          </span>
        </div>
        
        {couponDiscount > 0 && (
          <div className="cart-drawer__summary-line cart-drawer__summary-line--discount">
            <span>Discount</span>
            <span className="cart-drawer__discount-amount">
              -{formatPrice(couponDiscount)}
            </span>
          </div>
        )}
        
        <div className="cart-drawer__summary-line cart-drawer__summary-line--total">
          <span>Total</span>
          <span>{formatPrice(total)}</span>
        </div>
      </div>

      {/* {subtotal < 50 && (
        <div className="cart-drawer__shipping-notice">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>
            Add {formatPrice(50 - subtotal)} more for free shipping!
          </span>
        </div>
      )} */}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div
      className={`cart-drawer__backdrop ${className}`}
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cart-drawer-title"
    >
      <div
        ref={drawerRef}
        className="cart-drawer"
        tabIndex={-1}
        role="document"
      >
        <div className="cart-drawer__header">
          <h2 id="cart-drawer-title" className="cart-drawer__title">
            Shopping Cart
          </h2>
          <button
            className="cart-drawer__close"
            onClick={onClose}
            aria-label="Close cart"
            type="button"
          >
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="cart-drawer__content">
          {loading && (
            <div className="cart-drawer__loading">
              <LogoSpinner size="medium" text="Loading cart..." />
            </div>
          )}

          {error && (
            <div className="cart-drawer__error">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{error}</span>
              <Button
                onClick={async () => await operations.clearCart()}
                variant="secondary"
                size="small"
              >
                Clear Cart
              </Button>
            </div>
          )}

          {!loading && !error && isEmpty && renderEmptyState()}
          
          {!loading && !error && !isEmpty && (
            <>
              {renderCartItems()}
              {renderSummary()}
            </>
          )}
        </div>

        {!loading && !error && !isEmpty && (
          <div className="cart-drawer__footer">
            <div className="cart-drawer__footer-buttons">
              <Button
                onClick={handleViewCart}
                variant="secondary"
                size="medium"
                className="cart-drawer__view-cart-button"
              >
                View Cart
              </Button>
              <Button
                onClick={handleCheckout}
                variant="primary"
                size="large"
                className="cart-drawer__checkout-button"
                disabled={isEmpty}
              >
                Proceed to Checkout
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
